import React, { useState } from 'react';
import { X, ArrowRight, Zap, FileArchive } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Button } from './ui/Button';
import { useSetSetupCompleted } from '../api/useSetupConfig';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '../api/client';
import { Input } from './ui/Input';
import { toast } from 'sonner';
import type { TaxMode } from '../api/types';
import { ImportDumpStep } from './wizard/steps/ImportDumpStep';
import { RestoreSummaryStep } from './wizard/steps/RestoreSummaryStep';
import { BrandingStep } from './wizard/steps/BrandingStep';
import { SmtpStep } from './wizard/steps/SmtpStep';
import { NtfyStep } from './wizard/steps/NtfyStep';
import { CalendarStep } from './wizard/steps/CalendarStep';
import { ContractStep } from './wizard/steps/ContractStep';
import { ServicesStep } from './wizard/steps/ServicesStep';
import { CollaboratorsStep } from './wizard/steps/CollaboratorsStep';

type Step = 'welcome' | 'import-dump' | 'restore-summary' | 'studio' | 'branding'
  | 'invoice' | 'finance' | 'smtp' | 'ntfy' | 'calendar' | 'contract'
  | 'services' | 'collaborators' | 'complete';

type SetupPath = 'import' | 'guided' | 'quick';

interface StudioForm {
  studioName: string;
  studioPhone: string;
  studioWebsite: string;
  studioLogoUrl: string;
  brandColorPrimary: string;
  brandColorSecondary: string;
  ownerAddress: string;
  ownerCity: string;
  ownerVatNumber: string;
}

interface InvoiceForm {
  studioName: string;
  ownerVatNumber: string;
  ownerFiscalCode: string;
  ownerAddress: string;
  ownerCity: string;
  ownerIban: string;
}

interface FinanceForm {
  ownerDailyRate: string;
  defaultTaxMode: '' | TaxMode;
  defaultTaxRate: string;
  defaultTaxCoefficient: string;
}

interface ServiceItem {
  id: string;
  name: string;
  baseCost: string;
}

interface CollaboratorItem {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface SetupWizardProps {
  open: boolean;
  onClose: () => void;
}

const GUIDED_ORDER: Step[] = ['welcome', 'studio', 'branding', 'invoice', 'finance', 'smtp', 'ntfy', 'calendar', 'contract', 'services', 'collaborators', 'complete'];
const QUICK_ORDER: Step[] = ['welcome', 'studio', 'invoice', 'finance', 'complete'];
const IMPORT_ORDER: Step[] = ['welcome', 'import-dump', 'restore-summary', 'complete'];

const SKIPPABLE_STEPS: ReadonlySet<Step> = new Set(['branding', 'smtp', 'ntfy', 'calendar', 'contract', 'services', 'collaborators']);

const inputStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none', width: '100%',
};

const selectStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text-muted)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.5rem', outline: 'none', cursor: 'pointer',
};

const welcomeCardBase: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.75rem',
  padding: '1rem', borderRadius: 'var(--radius-card)',
  border: '1px solid rgba(255,255,255,0.10)',
  cursor: 'pointer', background: 'var(--glass-bg-card)',
  color: 'var(--color-text)',
};

export function SetupWizard({ open, onClose }: SetupWizardProps) {
  // ALL hooks unconditionally at the top
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('welcome');
  const [setupPath, setSetupPath] = useState<SetupPath | null>(null);
  const [studioForm, setStudioForm] = useState<StudioForm>({
    studioName: '', studioPhone: '', studioWebsite: '', studioLogoUrl: '',
    brandColorPrimary: '', brandColorSecondary: '',
    ownerAddress: '', ownerCity: '', ownerVatNumber: '',
  });
  const [invoiceForm, setInvoiceForm] = useState<InvoiceForm>({
    studioName: '', ownerVatNumber: '', ownerFiscalCode: '',
    ownerAddress: '', ownerCity: '', ownerIban: '',
  });
  const [financeForm, setFinanceForm] = useState<FinanceForm>({
    ownerDailyRate: '',
    defaultTaxMode: '',
    defaultTaxRate: '',
    defaultTaxCoefficient: '',
  });
  const [smtpForm, setSmtpForm] = useState({
    smtpHost: '', smtpPort: '', smtpUser: '', smtpPass: '', smtpFrom: '',
  });
  const [ntfyForm, setNtfyForm] = useState({
    ntfyServer: '', ntfyTopic: '', ntfyToken: '',
  });
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [collaborators, setCollaborators] = useState<CollaboratorItem[]>([]);

  // ALL useQuery calls here (unconditionally)
  const { data: studioData } = useQuery({
    queryKey: ['user-settings-studio'],
    queryFn: () => apiGet<StudioForm>('/user-settings/studio'),
    enabled: open && (step === 'studio' || step === 'branding'),
  });

  const { data: invoiceData } = useQuery({
    queryKey: ['user-settings-invoice'],
    queryFn: () => apiGet<InvoiceForm>('/user-settings/invoice'),
    enabled: open && (step === 'invoice'),
  });

  const { data: financeData } = useQuery({
    queryKey: ['user-settings-finance'],
    queryFn: () => apiGet<FinanceForm>('/user-settings/finance'),
    enabled: open && (step === 'finance'),
  });

  // ALL useEffect calls here (unconditionally)
  React.useEffect(() => {
    if (studioData && (step === 'studio' || step === 'branding')) {
      setStudioForm({
        studioName: studioData.studioName ?? '',
        studioPhone: studioData.studioPhone ?? '',
        studioWebsite: studioData.studioWebsite ?? '',
        studioLogoUrl: studioData.studioLogoUrl ?? '',
        brandColorPrimary: studioData.brandColorPrimary ?? '',
        brandColorSecondary: studioData.brandColorSecondary ?? '',
        ownerAddress: studioData.ownerAddress ?? '',
        ownerCity: studioData.ownerCity ?? '',
        ownerVatNumber: studioData.ownerVatNumber ?? '',
      });
    }
  }, [studioData, step]);

  React.useEffect(() => {
    if (invoiceData && step === 'invoice') {
      setInvoiceForm({
        studioName: invoiceData.studioName ?? '',
        ownerVatNumber: invoiceData.ownerVatNumber ?? '',
        ownerFiscalCode: invoiceData.ownerFiscalCode ?? '',
        ownerAddress: invoiceData.ownerAddress ?? '',
        ownerCity: invoiceData.ownerCity ?? '',
        ownerIban: invoiceData.ownerIban ?? '',
      });
    }
  }, [invoiceData, step]);

  React.useEffect(() => {
    if (financeData && step === 'finance') {
      setFinanceForm({
        ownerDailyRate: financeData.ownerDailyRate ? String(financeData.ownerDailyRate) : '',
        defaultTaxMode: financeData.defaultTaxMode ?? '',
        defaultTaxRate: financeData.defaultTaxRate ? String(financeData.defaultTaxRate) : '',
        defaultTaxCoefficient: financeData.defaultTaxCoefficient ? String(financeData.defaultTaxCoefficient) : '',
      });
    }
  }, [financeData, step]);

  // Helper functions (defined AFTER hooks, BEFORE mutations that reference them)
  const getOrder = (): Step[] => {
    if (setupPath === 'import') return IMPORT_ORDER;
    if (setupPath === 'guided') return GUIDED_ORDER;
    if (setupPath === 'quick') return QUICK_ORDER;
    return GUIDED_ORDER;
  };

  const getCurrentIndex = (): number => {
    return getOrder().indexOf(step);
  };

  const advanceStep = () => {
    const order = getOrder();
    const idx = getCurrentIndex();
    if (idx < order.length - 1) {
      setStep(order[idx + 1]);
    }
  };

  const goBack = () => {
    const order = getOrder();
    const idx = getCurrentIndex();
    if (idx > 0) {
      setStep(order[idx - 1]);
    }
  };

  const isSkippable = (s: Step): boolean => {
    return SKIPPABLE_STEPS.has(s);
  };

  // ALL useMutation calls here (unconditionally)
  const studioMutation = useMutation({
    mutationFn: () => apiPatch('/user-settings/studio', {
      studioName: studioForm.studioName || null,
      studioPhone: studioForm.studioPhone || null,
      studioWebsite: studioForm.studioWebsite || null,
      studioLogoUrl: studioForm.studioLogoUrl || null,
      brandColorPrimary: studioForm.brandColorPrimary || null,
      brandColorSecondary: studioForm.brandColorSecondary || null,
      ownerAddress: studioForm.ownerAddress || null,
      ownerCity: studioForm.ownerCity || null,
      ownerVatNumber: studioForm.ownerVatNumber || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-settings-studio'] });
      advanceStep();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const invoiceMutation = useMutation({
    mutationFn: () => apiPatch('/user-settings/invoice', {
      studioName: invoiceForm.studioName || null,
      ownerVatNumber: invoiceForm.ownerVatNumber || null,
      ownerFiscalCode: invoiceForm.ownerFiscalCode || null,
      ownerAddress: invoiceForm.ownerAddress || null,
      ownerCity: invoiceForm.ownerCity || null,
      ownerIban: invoiceForm.ownerIban || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-settings-invoice'] });
      advanceStep();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const financeMutation = useMutation({
    mutationFn: () => apiPatch('/user-settings/finance', {
      ownerDailyRate: financeForm.ownerDailyRate !== '' ? Number(financeForm.ownerDailyRate) : null,
      defaultTaxMode: financeForm.defaultTaxMode || null,
      defaultTaxRate: financeForm.defaultTaxMode && financeForm.defaultTaxRate !== '' ? Number(financeForm.defaultTaxRate) : null,
      defaultTaxCoefficient: financeForm.defaultTaxMode === 'FORFETTARIO' && financeForm.defaultTaxCoefficient !== '' ? Number(financeForm.defaultTaxCoefficient) : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-settings-finance'] });
      advanceStep();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setupMutation = useSetSetupCompleted();

  const handleChoosePath = (path: SetupPath) => {
    setSetupPath(path);
    setStep(path === 'import' ? 'import-dump' : 'studio');
  };

  const handleNext = () => {
    if (step === 'studio' || step === 'branding') {
      studioMutation.mutate();
    } else if (step === 'invoice') {
      invoiceMutation.mutate();
    } else if (step === 'finance') {
      financeMutation.mutate();
    } else {
      advanceStep();
    }
  };

  const handleSkip = () => {
    advanceStep();
  };

  const handleComplete = () => {
    const tasks: Promise<unknown>[] = [];
    if (setupPath === 'guided') {
      for (const svc of services) {
        if (!svc.name) continue;
        tasks.push(apiPost('/product-templates', {
          name: svc.name,
          category: 'ALTRO',
          baseCost: svc.baseCost ? Number(svc.baseCost) : null,
        }));
      }
      for (const collab of collaborators) {
        if (!collab.name || !collab.email) continue;
        tasks.push(apiPost('/collaborators', {
          name: collab.name, email: collab.email, role: collab.role,
        }));
      }
    }
    (tasks.length > 0 ? Promise.all(tasks) : Promise.resolve())
      .then(() => {
        setupMutation.mutate(true, {
          onSuccess: () => { toast.success('Setup completato!'); onClose(); },
        });
      })
      .catch(err => toast.error((err as Error).message));
  };

  const showTaxFields = financeForm.defaultTaxMode !== '';
  const showCoefficient = financeForm.defaultTaxMode === 'FORFETTARIO';

  const overlayRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Early return AFTER all hooks
  if (!open) return null;

  const order = getOrder();
  const currentIdx = getCurrentIndex();
  const totalSteps = order.length;

  const headerTitleMap: Record<Step, string> = {
    welcome: 'Benvenuto in LensCRM',
    'import-dump': 'Importa Backup',
    'restore-summary': 'Riepilogo Import',
    studio: 'Profilo Studio',
    branding: 'Branding',
    invoice: 'Dati Fatturazione',
    finance: 'Tariffe e Regime Fiscale',
    smtp: 'Server SMTP',
    ntfy: 'Notifiche Push',
    calendar: 'Google Calendar',
    contract: 'Template Contratto',
    services: 'Servizi Fotografici',
    collaborators: 'Collaboratori',
    complete: 'Setup Completato',
  };

  return createPortal(
    <div
      ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) return; }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'oklch(0 0 0 / 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        ref={contentRef}
        style={{
          background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-container)',
          width: '100%',
          maxWidth: 600,
          maxHeight: '90dvh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <div>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '0.25rem' }}>
              {headerTitleMap[step]}
            </h2>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
              Passo {currentIdx + 1} di {totalSteps}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-text-muted)', padding: '0.25rem',
              display: 'flex', alignItems: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {step === 'welcome' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
                Benvenuto in <strong>LensCRM</strong>. Scegli come iniziare:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div onClick={() => handleChoosePath('import')} style={welcomeCardBase}>
                  <FileArchive size={20} />
                  <span style={{ flex: 1, fontSize: 'var(--text-sm)' }}>Importa un backup esistente</span>
                  <ArrowRight size={16} />
                </div>
                <div onClick={() => handleChoosePath('guided')} style={welcomeCardBase}>
                  <ArrowRight size={20} />
                  <span style={{ flex: 1, fontSize: 'var(--text-sm)' }}>Setup guidato completo</span>
                  <ArrowRight size={16} />
                </div>
                <div onClick={() => handleChoosePath('quick')} style={welcomeCardBase}>
                  <Zap size={20} />
                  <span style={{ flex: 1, fontSize: 'var(--text-sm)' }}>Setup rapido</span>
                  <ArrowRight size={16} />
                </div>
              </div>
            </div>
          )}

          {step === 'import-dump' && (
            <ImportDumpStep onRestored={advanceStep} />
          )}

          {step === 'restore-summary' && (
            <RestoreSummaryStep onComplete={() => setStep('complete')} />
          )}

          {step === 'studio' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Input
                label="Nome studio"
                value={studioForm.studioName}
                onChange={e => setStudioForm(f => ({ ...f, studioName: e.target.value }))}
                placeholder="Studio Fotografico Rossi"
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <Input
                  label="Telefono"
                  value={studioForm.studioPhone}
                  onChange={e => setStudioForm(f => ({ ...f, studioPhone: e.target.value }))}
                  placeholder="+39 02 1234567"
                />
                <Input
                  label="Sito web"
                  value={studioForm.studioWebsite}
                  onChange={e => setStudioForm(f => ({ ...f, studioWebsite: e.target.value }))}
                  placeholder="https://studio.it"
                />
              </div>
              <Input
                label="Indirizzo"
                value={studioForm.ownerAddress}
                onChange={e => setStudioForm(f => ({ ...f, ownerAddress: e.target.value }))}
                placeholder="Via Roma 1"
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <Input
                  label="Città"
                  value={studioForm.ownerCity}
                  onChange={e => setStudioForm(f => ({ ...f, ownerCity: e.target.value }))}
                  placeholder="Milano"
                />
                <Input
                  label="P.IVA"
                  value={studioForm.ownerVatNumber}
                  onChange={e => setStudioForm(f => ({ ...f, ownerVatNumber: e.target.value }))}
                  placeholder="01234567890"
                />
              </div>
            </div>
          )}

          {step === 'branding' && (
            <BrandingStep
              form={{ brandColorPrimary: studioForm.brandColorPrimary, brandColorSecondary: studioForm.brandColorSecondary }}
              onChange={f => setStudioForm(sf => ({ ...sf, brandColorPrimary: f.brandColorPrimary, brandColorSecondary: f.brandColorSecondary }))}
            />
          )}

          {step === 'invoice' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Input
                label="Nome studio / Ragione sociale"
                value={invoiceForm.studioName}
                onChange={e => setInvoiceForm(f => ({ ...f, studioName: e.target.value }))}
                placeholder="es. Studio Fotografico Rossi"
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <Input
                  label="Partita IVA"
                  value={invoiceForm.ownerVatNumber}
                  onChange={e => setInvoiceForm(f => ({ ...f, ownerVatNumber: e.target.value }))}
                  placeholder="IT01234567890"
                />
                <Input
                  label="Codice fiscale"
                  value={invoiceForm.ownerFiscalCode}
                  onChange={e => setInvoiceForm(f => ({ ...f, ownerFiscalCode: e.target.value }))}
                  placeholder="RSSMRA80A01H501X"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <Input
                  label="Indirizzo"
                  value={invoiceForm.ownerAddress}
                  onChange={e => setInvoiceForm(f => ({ ...f, ownerAddress: e.target.value }))}
                  placeholder="Via Roma 1"
                />
                <Input
                  label="Città"
                  value={invoiceForm.ownerCity}
                  onChange={e => setInvoiceForm(f => ({ ...f, ownerCity: e.target.value }))}
                  placeholder="Milano"
                />
              </div>
              <Input
                label="IBAN"
                value={invoiceForm.ownerIban}
                onChange={e => setInvoiceForm(f => ({ ...f, ownerIban: e.target.value }))}
                placeholder="IT60X0542811101000000123456"
              />
            </div>
          )}

          {step === 'finance' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, marginBottom: '0.375rem' }}>
                  Compenso giornaliero titolare (€)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Es. 300"
                  value={financeForm.ownerDailyRate}
                  onChange={e => setFinanceForm(f => ({ ...f, ownerDailyRate: e.target.value }))}
                  style={{ ...inputStyle, maxWidth: '12rem' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, marginBottom: '0.375rem' }}>
                  Regime fiscale predefinito
                </label>
                <select
                  value={financeForm.defaultTaxMode}
                  onChange={e => setFinanceForm(f => ({ ...f, defaultTaxMode: e.target.value as '' | TaxMode, defaultTaxRate: '', defaultTaxCoefficient: '' }))}
                  style={{ ...selectStyle, minWidth: '14rem' }}
                >
                  <option value="">Nessuno (non calcolare imposte)</option>
                  <option value="FORFETTARIO">Forfettario</option>
                  <option value="ORDINARIO">Ordinario</option>
                </select>
              </div>

              {showTaxFields && (
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '10rem' }}>
                    <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, marginBottom: '0.375rem' }}>
                      Aliquota imposta (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="Es. 15"
                      value={financeForm.defaultTaxRate}
                      onChange={e => setFinanceForm(f => ({ ...f, defaultTaxRate: e.target.value }))}
                      style={{ ...inputStyle }}
                    />
                  </div>

                  {showCoefficient && (
                    <div style={{ flex: 1, minWidth: '10rem' }}>
                      <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 500, marginBottom: '0.375rem' }}>
                        Coefficiente di redditività (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        placeholder="Es. 78"
                        value={financeForm.defaultTaxCoefficient}
                        onChange={e => setFinanceForm(f => ({ ...f, defaultTaxCoefficient: e.target.value }))}
                        style={{ ...inputStyle }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 'smtp' && (
            <SmtpStep form={smtpForm} onChange={setSmtpForm} />
          )}

          {step === 'ntfy' && (
            <NtfyStep form={ntfyForm} onChange={setNtfyForm} />
          )}

          {step === 'calendar' && <CalendarStep />}

          {step === 'contract' && <ContractStep />}

          {step === 'services' && (
            <ServicesStep items={services} onChange={setServices} />
          )}

          {step === 'collaborators' && (
            <CollaboratorsStep items={collaborators} onChange={setCollaborators} />
          )}

          {step === 'complete' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>✨</div>
              <div>
                <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Setup completato!
                </h3>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                  LensCRM è ora pronto per l'uso. Puoi iniziare a gestire i tuoi eventi fotografici.
                </p>
              </div>
              <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1rem' }}>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginBottom: '0.5rem' }}>
                  Ricorda: puoi modificare tutti i dati in qualsiasi momento dalla sezione <strong>Impostazioni</strong>.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', gap: '0.5rem', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {step !== 'welcome' && (
              <Button variant="ghost" onClick={goBack}>
                Indietro
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {step !== 'complete' && step !== 'welcome' && step !== 'import-dump' && step !== 'restore-summary' && isSkippable(step) && (
              <Button variant="ghost" onClick={handleSkip}>
                Salta
              </Button>
            )}
            {step !== 'import-dump' && step !== 'restore-summary' && (
              <Button
                onClick={step === 'complete' ? handleComplete : handleNext}
                loading={studioMutation.isPending || invoiceMutation.isPending || financeMutation.isPending || setupMutation.isPending}
              >
                {step === 'complete' ? 'Inizia a usare LensCRM' : 'Avanti'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
